import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';
import Modal from '@chronos/components/Modal';

import { useBoardContext } from '@chronos/ui-toolkit/contexts';
import { useSaveBoardMemberSettingsMutation } from '@chronos/slices/settingsApi';
import Checkbox from '@chronos/components/Checkbox';
import WorklogForm from './WorklogForm';
import * as S from './styled';

const TITLE_MAP = {
  addWorklog: 'Add Worklog',
  editWorklog: 'Edit Worklog',
  startReview: 'Worklog Review',
  stopReview: 'Worklog Review',
};
const DONE_LABEL_MAP = {
  addWorklog: 'Save worklog',
  editWorklog: 'Save worklog',
  startReview: 'Start timer',
  stopReview: 'Stop timer',
};

const WorklogFormModal = ({
  isOpen,
  onClose,
  onSaved,
  title,
  ...formProps
}) => {
  const { boardId, boardMemberSettings } = useBoardContext();
  const [saveInProcess, setSaveInProcess] = useState(false);
  const formRef = useRef(null);
  const [saveSettings] = useSaveBoardMemberSettingsMutation();
  const doNotShowAgainMap = {
    startReview: !boardMemberSettings.showStartReview,
    stopReview: !boardMemberSettings.showStopReview,
  };
  const [doNotShowAgain, setDoNotShowAgain] = useState(
    doNotShowAgainMap[formProps.mode]
  );

  const onSaving = useCallback(() => {
    setSaveInProcess(true);
  }, []);

  const handleSavedWorklog = useCallback(
    async ({ values, response }) => {
      try {
        const showReview = !doNotShowAgain;
        let reviewSettingsKey = '';
        if (['startReview', 'stopReview'].includes(formProps.mode)) {
          reviewSettingsKey =
            formProps.mode === 'startReview'
              ? 'showStartReview'
              : 'showStopReview';
        }
        if (
          Object.hasOwn(boardMemberSettings, reviewSettingsKey) &&
          boardMemberSettings[reviewSettingsKey] !== showReview
        ) {
          saveSettings({
            body: {
              boardId,
              [reviewSettingsKey]: showReview,
            },
          });
        }

        if (onSaved) {
          await onSaved({ values, response });
        }
      } catch (err) {
        Sentry.captureException(err);
        console.error(err);
      }

      setSaveInProcess(false);
      onClose('done', { mode: formProps.mode });
    },
    [doNotShowAgain, formProps.mode, boardId, boardMemberSettings, onSaved]
  );

  const handleSubmit = () => {
    if (formRef.current) {
      // call submit on child form
      formRef.current.submit();
    }
  };

  const handleClose = () => {
    onClose('cancel', { mode: formProps.mode });
  };

  // TODO: liveWorklogs.find((lw) => lw.id === worklogId)?.organizationId
  const liveWorklogOrganizationId = null;
  return (
    <Modal
      isOpen={isOpen}
      isSaving={saveInProcess}
      handleClose={handleClose}
      handleSubmit={handleSubmit}
      isMobile
      title={title || TITLE_MAP[formProps.mode]}
      doneLabel={DONE_LABEL_MAP[formProps.mode]}
    >
      <WorklogForm
        liveWorklogOrganizationId={liveWorklogOrganizationId}
        onSaved={handleSavedWorklog}
        onSaving={onSaving}
        mode={formProps.mode}
        {...formProps}
        ref={formRef}
      />
      <S.ModalFooter>
        {['startReview', 'stopReview'].includes(formProps.mode) && (
          <Checkbox
            name="do_not_show_again"
            id="do_not_show_again"
            label="Do not show this window again"
            isChecked={doNotShowAgain}
            onChange={() => {
              setDoNotShowAgain((v) => !v);
            }}
          />
        )}
      </S.ModalFooter>
    </Modal>
  );
};

WorklogFormModal.defaultProps = {
  isOpen: false,
  mode: '',
  adjustStartTime: true,
  title: '',
};

WorklogFormModal.propTypes = {
  title: PropTypes.string,
  cardId: PropTypes.string,
  lastUsedCardId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  isOpen: PropTypes.bool,
  adjustStartTime: PropTypes.bool,
  mode: PropTypes.oneOf([
    'addWorklog',
    'editWorklog',
    'startReview',
    'stopReview',
  ]),
};

export default WorklogFormModal;
